import type { InstanceApi } from '@eis/insuranceservice-sdk';
import {
  missingRequiredPartnerFields,
  partnerCustomFieldsOf,
  partnerFieldValuesOf,
  partnerTypeFieldGroups,
  platformPartnerTypeSlug,
  validationIssuesOf,
  type PartnerFieldValues,
  type PmmPartnerType,
} from '@eis/core';
import {
  codeOf,
  linksOf,
  seedPartnerValues,
  type PartnerFormValues,
  type PartnerLink,
  type PartnerRoleRule,
} from '../../policies/model/partners';
import type { Lead, PartnerDraft } from './types';

/**
 * The lead form's partner state (SDD-013 §8.1.5): every pinned role can be filled by a partner
 * PICKER — a link to a partner the Partner service knows — or by a DRAFT — a NEW partner, typed
 * and filled in from the live type's own fields (SDD-014 PS-8), which the accept registers
 * (PS-5). The service counts both towards the role's cardinality and refuses a role filled both
 * ways (PARTNER_ROLE_DUPLICATE), so the form offers one mode per role.
 *
 * A draft is written in the Partner service's own create shape — `partnerTypeCode` (the live
 * type's code) and `customFields` — never as a name the registry would have to match (O-5). The
 * golden member lead still carries SDD-013's `{typeCode, name}` spelling; the form reads both and
 * writes the service's, falling back to the old one only while the type catalog is unreachable.
 */

export type PartnerMode = 'id' | 'draft';

export interface DraftValues {
  /** Platform-spelled type slug (`person`, `organisation` — STOP-3), chosen from the live catalog. */
  readonly typeCode: string;
  /** The type's fields, flat (`BasicInformation.firstName`, …) — what `PartnerFieldsForm` edits. */
  readonly fields: PartnerFieldValues;
}

export interface LeadPartnerState {
  readonly mode: Readonly<Record<string, PartnerMode>>;
  readonly ids: PartnerFormValues;
  readonly drafts: Readonly<Record<string, DraftValues>>;
}

/** The type a role's draft defaults to: the role's first allowed PMM type, else `person`. */
export const defaultTypeFor = (rule: PartnerRoleRule): string => rule.partnerTypes?.[0] ?? 'person';

/** The live type with this platform-spelled slug, if the catalog answered. */
export const typeForSlug = (types: readonly PmmPartnerType[], slug: string): PmmPartnerType | undefined =>
  types.find((type) => platformPartnerTypeSlug(type.slug) === slug);

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

/** A person's name split the way the service derives `displayName` back (PROBE.md §6). */
function fieldsFromName(name: string, slug: string): PartnerFieldValues {
  const trimmed = name.trim().replace(/\s+/g, ' ');
  if (trimmed === '') return {};
  if (slug === 'person') {
    const at = trimmed.lastIndexOf(' ');
    return at === -1
      ? { 'BasicInformation.firstName': trimmed }
      : { 'BasicInformation.firstName': trimmed.slice(0, at), 'BasicInformation.lastName': trimmed.slice(at + 1) };
  }
  return { 'BasicInformation.organizationName': trimmed };
}

/** Any stored `customFields`, flattened onto dotted keys — for a type whose groups are not in hand. */
function flattenCustomFields(value: unknown, prefix = ''): Record<string, string> {
  const out: Record<string, string> = {};
  if (typeof value !== 'object' || value === null) return out;
  const entries = Array.isArray(value) ? value.map((item, index) => [String(index), item] as const) : Object.entries(value);
  for (const [key, child] of entries) {
    const path = prefix === '' ? key : `${prefix}.${key}`;
    if (typeof child === 'object' && child !== null) Object.assign(out, flattenCustomFields(child, path));
    else if (child !== undefined) out[path] = typeof child === 'string' ? child : String(child as number | boolean);
  }
  return out;
}

/** The display name a draft's fields derive to — what the lead shows for a not-yet-registered party. */
export function draftDisplayName(fields: PartnerFieldValues): string {
  const organisation = fields['BasicInformation.organizationName'] ?? '';
  if (organisation.trim() !== '') return organisation.trim();
  return `${fields['BasicInformation.firstName'] ?? ''} ${fields['BasicInformation.lastName'] ?? ''}`.trim();
}

/** Seeds the state from a stored lead (edit) or from nothing (create); either draft spelling is read. */
export function seedPartnerState(
  rules: readonly PartnerRoleRule[],
  lead: Lead | undefined,
  types: readonly PmmPartnerType[] = [],
): LeadPartnerState {
  const links: PartnerLink[] = (lead?.draft?.partners ?? [])
    .map((partner) => partner.link)
    .filter((link): link is PartnerLink => link !== undefined);
  const ids = seedPartnerValues(rules, links);
  const mode: Record<string, PartnerMode> = {};
  const drafts: Record<string, DraftValues> = {};
  for (const rule of rules) {
    const roleErn = rule.roleErn ?? '';
    const draft = (lead?.partnerDrafts ?? []).find((candidate) => candidate.roleErn === roleErn);
    const partner = draft?.partner ?? {};
    const byCode = types.find((type) => type.code === text(partner.partnerTypeCode));
    const typeCode =
      text(partner.typeCode) !== ''
        ? text(partner.typeCode)
        : byCode !== undefined
          ? platformPartnerTypeSlug(byCode.slug)
          : defaultTypeFor(rule);
    const type = typeForSlug(types, typeCode);
    const customFields = partner.customFields;
    const fields: PartnerFieldValues =
      typeof customFields === 'object' && customFields !== null
        ? type === undefined
          ? flattenCustomFields(customFields)
          : partnerFieldValuesOf(partnerTypeFieldGroups(type), customFields as Record<string, unknown>)
        : fieldsFromName(text(partner.name), typeCode);
    drafts[roleErn] = { typeCode, fields };
    mode[roleErn] = draft === undefined ? 'id' : 'draft';
  }
  return { mode, ids, drafts };
}

/** The `partners` a write carries: the roles in id mode, blanks dropped (P-7). */
export function partnerLinksOf(rules: readonly PartnerRoleRule[], state: LeadPartnerState): PartnerLink[] {
  const idRoles = rules.filter((rule) => state.mode[rule.roleErn ?? ''] !== 'draft');
  return linksOf(idRoles, state.ids);
}

/**
 * The `partnerDrafts` a write carries: the roles in draft mode, in the Partner service's own create
 * shape (PS-8) — the live type's CODE and the document — tagged with the role. While the type
 * catalog is unreachable the SDD-013 spelling (`typeCode` slug + derived `name`) is written instead,
 * which the registry accepts too (adapters/partnerpmm/registry.go); a draft with nothing filled in
 * is not written at all.
 */
export function partnerDraftsOf(
  rules: readonly PartnerRoleRule[],
  state: LeadPartnerState,
  types: readonly PmmPartnerType[] = [],
): PartnerDraft[] {
  const out: PartnerDraft[] = [];
  for (const rule of rules) {
    const roleErn = rule.roleErn ?? '';
    if (state.mode[roleErn] !== 'draft') continue;
    const draft = state.drafts[roleErn];
    if (draft === undefined) continue;
    const filled = Object.values(draft.fields).some((value) => value.trim() !== '');
    if (!filled) continue;
    const type = typeForSlug(types, draft.typeCode);
    const tags = [codeOf(roleErn)];
    if (type === undefined) {
      const name = draftDisplayName(draft.fields);
      if (name === '') continue;
      out.push({ roleErn, partner: { typeCode: draft.typeCode, name, tags } });
      continue;
    }
    out.push({
      roleErn,
      partner: {
        partnerTypeCode: type.code,
        customFields: partnerCustomFieldsOf(partnerTypeFieldGroups(type), draft.fields),
        tags,
      },
    });
  }
  return out;
}

/**
 * The roles whose draft is in draft mode but not fillable yet — a required role's missing input:
 * with the live type in hand, its required fields (`isRequired`); without it, a name.
 */
export function incompleteDrafts(
  rules: readonly PartnerRoleRule[],
  state: LeadPartnerState,
  types: readonly PmmPartnerType[] = [],
): string[] {
  return rules
    .filter((rule) => state.mode[rule.roleErn ?? ''] === 'draft' && (rule.minCount ?? 0) > 0)
    .filter((rule) => {
      const draft = state.drafts[rule.roleErn ?? ''];
      if (draft === undefined) return true;
      const type = typeForSlug(types, draft.typeCode);
      if (type === undefined) return draftDisplayName(draft.fields) === '';
      return missingRequiredPartnerFields(partnerTypeFieldGroups(type), draft.fields).length > 0;
    })
    .map((rule) => rule.roleErn ?? '');
}

/** The required inputs of one role's draft still empty — for the form's markers. */
export function missingDraftFields(draft: DraftValues, types: readonly PmmPartnerType[]): string[] {
  const type = typeForSlug(types, draft.typeCode);
  return type === undefined ? [] : missingRequiredPartnerFields(partnerTypeFieldGroups(type), draft.fields);
}

/**
 * A refusal about the DRAFTS (`/partnerDrafts/<i>/…`, PARTNER_ROLE_DUPLICATE), as messages —
 * the link refusals are `partnerRefusalOf`'s (SDD-011 §6).
 */
export function draftRefusalOf(error: unknown): string[] {
  return validationIssuesOf(error)
    .map((issue) => ({ path: issue.path ?? '', message: issue.message ?? '' }))
    .filter((issue) => issue.path.startsWith('/partnerDrafts'))
    .map((issue) => issue.message);
}

/** The create-policy body a lead form sends: the tariff, the start, the payload, the links. */
export function policyRequestOf(
  tarifErn: string,
  parentPolicyCode: string,
  effectiveFrom: string,
  data: Record<string, unknown>,
  partners: readonly PartnerLink[],
): InstanceApi.CreatePolicyRequest {
  return {
    tarifErn,
    ...(parentPolicyCode === '' ? {} : { parentPolicyCode }),
    effectiveFrom,
    data,
    partners: [...partners],
  };
}
